import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { Level, LogEvent } from 'pino';
import type { DatadogConfig, DatadogLogger } from './types';

type Logger = DatadogLogger | Console;

let isInitialized = false;

export const baseConfig = {
  trackSessionAcrossSubdomains: true,
  useCrossSiteSessionCookie: true,
  useSecureSessionCookie: true,
  forwardErrorsToLogs: true,
  sampleRate: 100
} as Partial<LogsInitConfiguration>;

function isConfig(value: DatadogLogger | DatadogConfig): value is DatadogConfig {
  return 'clientToken' in value && 'applicationId' in value;
}

export function createDatadogSend(name: string, datadog: DatadogLogger | DatadogConfig) {
  const logger = isConfig(datadog) ? getLogger(name, datadog) : datadog;

  return (level: Level, logEvent: LogEvent): void => {
    Promise.resolve(logger).then(LOG => {
      const [message, context, error] = format(logEvent);
      logMessage(LOG, level, [message, context, error]);
    });
  };
}

function isRecord(value: unknown): value is Record<string | number | symbol, unknown> {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
}

export function logMessage(LOG: Logger, level: Level, [message, context, error]: [string, object, Error?]): void {
  switch (level) {
    case 'trace':
    case 'debug':
      LOG.debug(message, context, error);
      break;
    case 'info':
      LOG.info(message, context, error);
      break;
    case 'warn':
      LOG.warn(message, context, error);
      break;
    case 'error':
    case 'fatal':
      LOG.error(message, context, error);
      break;
    default:
      LOG.info(message, context, error);
  }
}

function parse(logEvent: LogEvent): [string, object?] {
  let message;
  let mergingObject;

  if (logEvent.messages.length === 1) {
    // The value in the first position of messages may not be a string e.g. logger.info({ foo: 'bar' });
    [message] = logEvent.messages;
    if (typeof message !== 'string') {
      mergingObject = message;
      message = '';
    }
  } else {
    [mergingObject, message] = logEvent.messages;
  }

  return [message, mergingObject];
}

/**
 * Extracts error from `obj`. Returns a tuple including error if `obj` itself is a error
 * or an error is nested as a property of the object. Otherwise, returns the object itself.
 */
function extractError(obj: object): [object, Error?] {
  if (obj instanceof Error) {
    return [{}, obj];
  }

  for (const [key, value] of Object.entries(obj)) {
    if (value instanceof Error) {
      const { [key]: _, ...rest } = obj as { [key: string]: unknown };
      return [rest, value];
    }
  }

  return [obj];
}

export function format(logEvent: LogEvent): [string, object, Error?] {
  let [message, mergingObject] = parse(logEvent); // eslint-disable-line prefer-const

  if (!isRecord(mergingObject)) {
    // when merging object is an array or primitive we have to nest it
    mergingObject = { payload: mergingObject };
  }

  // Merge bindings into a single object
  const bindings = logEvent.bindings.reduce((acc, obj) => {
    Object.assign(acc, obj);
    return acc;
  }, {});

  const [data, error] = extractError(mergingObject);

  const context = {
    timestamp: logEvent.ts,
    url: window.document.URL,
    browser: true,
    ...bindings,
    ...data
  };

  return [message, context, error];
}

export async function getLogger(name: string, config: DatadogConfig): Promise<DatadogLogger | Console> {
  let logger: DatadogLogger | Console = console;

  try {
    const { datadogLogs } = await import('@datadog/browser-logs');

    // Only initialize Datadog browser client once
    if (!isInitialized) {
      datadogLogs.init({
        ...baseConfig,
        ...config
      });
      isInitialized = true;
    }

    logger = name ? datadogLogs.createLogger(name) : datadogLogs.logger;
  } catch (err) {
    console.error('Could not load datadog client', err); // eslint-disable-line no-console
  }

  return logger;
}
